// One exchange in the workspace chat: the user's message, then Bump AI's answer — waiting,
// streaming, finished (with copy and rating), or failed (with retry).
import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck, faThumbsUp, faThumbsDown, faRotateRight, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';

import bumpMark from '../../../assets/images/logo/bumpups-green-logo.png';
import useSmoothText from './useSmoothText';

const markdownComponents = {
  code({ node, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || '');
    return match ? (
      <SyntaxHighlighter style={dracula} language={match[1]} PreTag="div" {...props}>
        {String(children).replace(/\n$/, '')}
      </SyntaxHighlighter>
    ) : (
      <code className={className} {...props}>{children}</code>
    );
  },
  a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer">{props.children}</a>,
};

const toMillis = ts => (ts && typeof ts.toMillis === 'function' ? ts.toMillis() : null);

// What Bump AI is doing while the user waits. Watching a YouTube video takes 30–60 seconds on
// the first message, so the line changes to show it's still working. `since` is when the server
// started on this message (null until it has), so a retry or a reload measures the current wait.
function useWaitingLabel(active, since, isYouTube) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const start = Math.min(since || Date.now(), Date.now());
    const tick = () => setElapsed((Date.now() - start) / 1000);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active, since]);
  if (!isYouTube) return elapsed < 20 ? 'Thinking' : 'Still working on it';
  if (elapsed < 15) return 'Watching the video';
  if (elapsed < 45) return 'Thinking it through';
  return 'Still working, longer videos take a little more time';
}

function CopyButton({ text, label, className }) {
  const [copied, setCopied] = useState(false);
  const copy = () => navigator.clipboard.writeText(text).then(() => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }).catch(() => {});
  return (
    <button type="button" className={`${className} ${copied ? 'is-copied' : ''}`} onClick={copy} aria-label={copied ? 'Copied' : label} title={copied ? 'Copied' : label}>
      <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
      {className === 'wsc-action' && <span>{copied ? 'Copied' : 'Copy'}</span>}
    </button>
  );
}

export default function ChatMessage({ turn, isYouTube, isLast, canRetry, onRetry, rating, onRate }) {
  const waiting = turn.status === 'sending' || turn.status === 'pending';
  const streaming = turn.status === 'streaming';
  // Keep animating an answer the user watched arrive, through its final snapshot.
  const watchedLive = useRef(waiting || streaming);
  if (waiting || streaming) watchedLive.current = true;

  const smooth = useSmoothText(turn.answer || '', watchedLive.current);
  const serverSince = turn.status === 'pending' ? toMillis(turn.retried_at) || toMillis(turn.created_at) : null;
  const waitingLabel = useWaitingLabel(waiting, serverSince, isYouTube);
  const typing = streaming || (turn.status === 'done' && !smooth.done);

  return (
    <div className="wsc-turn">
      <div className="wsc-user">
        <div className="wsc-user-bubble">{turn.prompt}</div>
        <CopyButton text={turn.prompt} label="Copy message" className="wsc-user-copy" />
      </div>

      <div className="wsc-bot">
        <img src={bumpMark} alt="" className={`wsc-bot-mark ${waiting || typing ? 'is-live' : ''}`} />
        <div className="wsc-bot-body">
          {waiting && (
            <div className="wsc-waiting" role="status" aria-live="polite">
              <span className="wsc-dots" aria-hidden="true"><i /><i /><i /></span>
              <span>{waitingLabel}</span>
            </div>
          )}

          {(streaming || turn.status === 'done') && (
            <div className={`wsc-answer ${typing ? 'is-typing' : ''}`} aria-busy={typing}>
              <ReactMarkdown className="wsc-markdown" remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {smooth.text}
              </ReactMarkdown>
            </div>
          )}

          {turn.status === 'done' && !typing && (
            <div className={`wsc-actions ${isLast ? 'is-last' : ''}`}>
              <CopyButton text={turn.answer} label="Copy answer" className="wsc-action" />
              <button type="button" className={`wsc-action wsc-rate ${rating === 'like' ? 'is-liked' : ''}`} onClick={() => onRate(rating === 'like' ? null : 'like')} aria-label="Helpful" aria-pressed={rating === 'like'} title="Helpful">
                <FontAwesomeIcon icon={faThumbsUp} />
              </button>
              <button type="button" className={`wsc-action wsc-rate ${rating === 'dislike' ? 'is-disliked' : ''}`} onClick={() => onRate(rating === 'dislike' ? null : 'dislike')} aria-label="Not helpful" aria-pressed={rating === 'dislike'} title="Not helpful">
                <FontAwesomeIcon icon={faThumbsDown} />
              </button>
            </div>
          )}

          {turn.status === 'error' && (
            <div className="wsc-failed" role="alert">
              <FontAwesomeIcon icon={faCircleExclamation} className="wsc-failed-icon" />
              <div className="wsc-failed-text">
                <p>{turn.error || 'This message could not be answered.'}</p>
                {isLast && canRetry && (
                  <button type="button" className="wsc-retry" onClick={onRetry}>
                    <FontAwesomeIcon icon={faRotateRight} /> Try again
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
